import { useStore } from 'vuex'
import { computed, onMounted, ref, watch } from 'vue'
import debounce from 'foundation/js/debounce'

export default {
    name: 'DirectMessageNavigation',
    props: {
        guid: {
            type: String,
            required: true
        },
        userData: {
            type: Object,
            required: true
        },
        chatId: {
            type: String,
            required: false
        },
        messagesPerLoad: {
            type: Number,
            required: false
        }
    },
    setup(props) {
        const store = useStore()

        if (props.messagesPerLoad > 0) {
            store.commit(
                'forum/set_direct_message_threads_take',
                props.messagesPerLoad
            )
        }
        const inputText = ref('')
        const results = ref([])
        const showSpinner = computed(() => store.state.forum.is_submitting)
        const allThreads = computed(
            () => store.state.forum.direct_message_threads
        )
        const totalMessageThreads = computed(
            () => store.state.forum.direct_message_threads_data
        )
        const skip = ref(0)
        const take = computed(
            () => store.state.forum.direct_message_threads_take
        )
        const getMoreMessageThreads = ref(false)
        var profileId = ''

        onMounted(async () => {
            if (store.state.forum.direct_messages_bootstrapped) return
            store.commit('forum/set_direct_messages_bootstrapped', true)

            const newChatRecipient = new URLSearchParams(
                document.location.search
            ).get('newChatRecipient')

            if (newChatRecipient) {
                const [existingChatId] = await Promise.all([
                    store.dispatch('forum/findChatWithUser', {
                        profileId: newChatRecipient
                    }),
                    store.dispatch('forum/getDirectMessageThreads', {
                        userId: props.userData.userId,
                        skip: skip.value,
                        take: take.value,
                        loadFirstChat: false
                    })
                ])

                if (existingChatId) {
                    store.dispatch('forum/getDirectMessageThreadData', {
                        chatId: existingChatId
                    })
                } else {
                    store.dispatch('forum/setupNewChatRecipient', {
                        profileId: newChatRecipient
                    })
                }
            } else {
                profileId = new URLSearchParams(document.location.search).get(
                    'profile'
                )
                getDirectMessageThreads(true)
            }
        })

        watch(
            () => allThreads.value,
            (newValue) => {
                if (!newValue) return
                results.value = newValue
                getMoreMessageThreads.value =
                    newValue.length < (totalMessageThreads.value.totalHits ?? 0)
            }
        )

        const scrollTreadInToView = function () {
            const candidates = document.querySelectorAll(
                '.kb-direct-messages-content--big-screens .kb-direct-messages-content__right-wrapper, .kb-direct-messages-content--smaller-screens .kb-direct-messages-content__left-wrapper'
            )
            for (const thread of candidates) {
                if (thread.offsetParent !== null) {
                    thread.scrollIntoView({ behavior: 'smooth' })
                    return
                }
            }
        }

        const handleInputText = debounce(() => {
            searchMessages()
        }, 500)

        const searchMessages = function () {
            if (inputText.value === '') {
                results.value = allThreads.value
                return
            }

            const searchResults = []
            allThreads.value.map((result) => {
                if (
                    result.chatName
                        .toLowerCase()
                        .includes(inputText.value.toLowerCase())
                ) {
                    searchResults.push(result)
                }
            })

            results.value = searchResults
        }

        const getDirectMessageThreads = function (loadFirstChat = false) {
            store.dispatch('forum/getDirectMessageThreads', {
                userId: props.userData.userId,
                chatId: props.chatId,
                toProfileId: profileId,
                skip: skip.value,
                take: take.value,
                loadFirstChat: loadFirstChat
            })
        }

        const loadMoreMessageThreads = function () {
            skip.value = take.value + skip.value
            getDirectMessageThreads()
        }

        const startNewMessage = function () {
            store.dispatch('forum/clearDirectMessageThread')
            scrollTreadInToView()
        }

        const changeChat = function (chatId) {
            store.dispatch('forum/clearDirectMessageThread')

            store.dispatch('forum/getDirectMessageThreadData', {
                chatId,
                userId: props.userData.userId
            })
            scrollTreadInToView()
        }

        return {
            showSpinner,
            inputText,
            results,
            getMoreMessageThreads,
            handleInputText,
            searchMessages,
            loadMoreMessageThreads,
            startNewMessage,
            changeChat,
            getLabel: (labelKey) =>
                store.getters['labels/getLabel']({
                    labelCollection: 'DirectMessageNavigation',
                    labelKey
                })
        }
    }
}
